import React, { useRef, useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Paper,
    CircularProgress,
    Tooltip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import {
    Print,
    Close,
    Send,
    LocalShipping,
    ContentCopy,
    Edit,
    Image as ImageIcon,
    TextSnippet,
    CheckCircle,
} from "@mui/icons-material";
import { formatCurrencyBDT } from "../../../utils/currency";
import { getCustomerFullName } from "../../../utils/customerUtils";
import { Order, Customer } from "../../../types";
import { telegramService } from "../../../services/telegramService";
import { RECEIPT_CONFIG } from "../../../config/receipt";
import { copyElementToClipboard } from "../../../utils/exportCardImage";
import { PRINT_SHORTCUT_LABEL, isPrintShortcut } from "../../../utils/keyboard";

interface ReceiptModalProps {
    open: boolean;
    onClose: () => void;
    order: Order | null;
    customer: Customer | null;
    paymentMethod: string;
    amountPaid: number;
    onCreateCourier?: () => void;
    onDuplicateOrder?: () => void;
    onEdit?: () => void;
    onUpdateStatus?: () => void;
    autoPrint?: boolean;
}

export const ReceiptModal: React.FC<ReceiptModalProps> = ({
    open,
    onClose,
    order,
    customer,
    amountPaid,
    onCreateCourier,
    onDuplicateOrder,
    onEdit,
    onUpdateStatus,
    autoPrint,
}) => {
    const receiptRef = useRef<HTMLDivElement>(null);
    const [copyingReceipt, setCopyingReceipt] = useState(false);
    const [copiedReceipt, setCopiedReceipt] = useState(false);
    const [copyMenuAnchor, setCopyMenuAnchor] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (!open || !autoPrint) return;
        let rafId: number;
        rafId = requestAnimationFrame(() => {
            handlePrint();
            onClose();
        });
        return () => cancelAnimationFrame(rafId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, autoPrint]);

    const handlePrint = () => {
        if (receiptRef.current) {
            try {
                console.log("Starting print process...");

                const printWindow = window.open(
                    "",
                    "_blank",
                    "width=400,height=600,scrollbars=no,resizable=no",
                );
                if (!printWindow) {
                    alert(
                        "Print blocked by popup blocker. Please allow popups for this site and try again.",
                    );
                    return;
                }

                const rawContent = receiptRef.current.innerHTML;
                const receiptContent = rawContent.replace(
                    /font-family\s*:[^;"}]+[;]?/gi,
                    "",
                );
                console.log("Receipt content captured");

                printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Receipt - ${order?.order_number}</title>
              <style>
                * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
                  font-family: Arial, Helvetica, sans-serif !important;
                }
                body {
                  font-family: Arial, Helvetica, sans-serif !important;
                  margin: 0;
                  padding: 20px;
                  font-size: 12px;
                  line-height: 1.4;
                  background: white;
                  color: black;
                }
                .receipt {
                  max-width: none;
                  width: 100%;
                  margin: 0;
                  padding: 0;
                }
                .header {
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-start;
                  margin-bottom: 20px;
                  min-height: 60px;
                }
                .logo-section {
                  flex: 0 0 auto;
                  width: 25%;
                  text-align: center;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }
                .logo-section img {
                  width: 60px;
                  height: 60px;
                  object-fit: contain;
                }
                .content-section {
                  flex: 1 1 auto;
                  width: 75%;
                  text-align: center;
                  border-left: 1px solid #ccc;
                  padding-left: 8px;
                }
                .shop-name {
                  font-size: 18px;
                  font-weight: bold;
                  margin-bottom: 5px;
                }
                .shop-info {
                  font-size: 10px;
                  margin-bottom: 2px;
                }
                .divider {
                  border-top: 1px dashed #000;
                  margin: 10px 0;
                }
                .order-info {
                  margin-bottom: 15px;
                }
                .items-table {
                  width: 100%;
                  margin-bottom: 15px;
                }
                .items-table td {
                  padding: 2px 0;
                  vertical-align: top;
                  border: none;
                }
                .item-name {
                  font-weight: bold;
                }
                .item-details {
                  font-size: 10px;
                  color: #666;
                }
                .totals {
                  margin-top: 15px;
                  width: 100% !important;
                }
                .totals table {
                  width: 100% !important;
                  border-collapse: collapse !important;
                }
                .totals td {
                  padding: 1px 0;
                  vertical-align: top;
                  border: none;
                }
                .totals td:last-child {
                  text-align: right;
                }
                .totals .total-row td {
                  font-size: 14px;
                  font-weight: bold;
                  border-top: 1px solid #000 !important;
                  padding-top: 10px;
                }
                .totals .grand-total {
                  font-weight: bold;
                  font-size: 14px;
                  border-top: 1px solid #000;
                  padding-top: 5px;
                  margin-top: 10px;
                }
                .footer {
                  text-align: center;
                  margin-top: 20px;
                  font-size: 10px;
                }
                @media print {
                  body {
                    margin: 0;
                    padding: 10px;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
                  .receipt {
                    max-width: none !important;
                    width: 100% !important;
                    margin: 0 !important;
                    padding: 0 !important;
                  }
                  .header {
                    display: flex !important;
                    justify-content: space-between !important;
                    align-items: flex-start !important;
                    margin-bottom: 20px;
                    min-height: 60px;
                  }
                  .logo-section {
                    flex: 0 0 auto !important;
                    width: 25% !important;
                    text-align: center !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                  }
                  .content-section {
                    flex: 1 1 auto !important;
                    width: 75% !important;
                    padding-left: 8px !important;
                    text-align: left !important;
                    border-left: 1px solid #ccc !important;
                  }
                  @page {
                    margin: 0.5cm;
                    size: auto;
                  }
                }
                @media screen {
                  body {
                    background: #f5f5f5;
                    padding: 20px;
                  }
                  .receipt {
                    background: white;
                    padding: 20px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.1);
                  }
                }
              </style>
            </head>
            <body>
              ${receiptContent}
              <script>
                window.onload = function() {
                  console.log('Print window loaded');
                  setTimeout(function() {
                    window.print();
                  }, 500);
                };
                
                window.onafterprint = function() {
                  console.log('Print dialog closed');
                  setTimeout(function() {
                    window.close();
                  }, 100);
                };
              </script>
            </body>
          </html>
        `);

                printWindow.document.close();
                console.log("Print window document written and closed");
            } catch (error) {
                console.error("Print error:", error);
                alert(
                    "Print failed. Error: " +
                        (error instanceof Error
                            ? error.message
                            : String(error)),
                );
            }
        } else {
            console.error("Receipt element not found");
            alert("Receipt not ready for printing. Please try again.");
        }
    };

    // Ctrl/⌘+P presses Print — the receipt-only print window — instead of the
    // browser printing the whole page behind the dialog. Wherever a receipt
    // is open: POS, orders, order drawer. Captured early on the window so it
    // wins over the browser's own shortcut.
    const printRef = useRef(handlePrint);
    useEffect(() => {
        printRef.current = handlePrint;
    });
    useEffect(() => {
        if (!open || autoPrint) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (!isPrintShortcut(e)) return;
            e.preventDefault();
            e.stopPropagation();
            if (!e.repeat) printRef.current(); // holding the keys opens one window, not many
        };
        window.addEventListener("keydown", onKeyDown, true);
        return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [open, autoPrint]);

    // Shared loading/success/error state machine for both copy modes below
    // — the only thing that differs between "copy as photo" and "copy as
    // text" is the async action itself, so that's the one thing callers
    // supply.
    const runCopyAction = async (action: () => Promise<void>) => {
        setCopyingReceipt(true);
        try {
            await action();
            setCopiedReceipt(true);
            setTimeout(() => setCopiedReceipt(false), 2000);
        } catch (error) {
            console.error("Failed to copy receipt:", error);
            alert(
                "Failed to copy receipt. Error: " +
                    (error instanceof Error ? error.message : String(error)),
            );
        } finally {
            setCopyingReceipt(false);
        }
    };

    // Same mechanism as the Price List Generator's per-card Copy button
    // (see exportCardImage.ts) — rasterizes the receipt Paper to a PNG and
    // writes it straight to the OS clipboard, so it can be pasted directly
    // into WhatsApp/Messenger without a download-then-attach round trip.
    const handleCopyImage = () => {
        if (!receiptRef.current) {
            alert("Receipt not ready to copy. Please try again.");
            return;
        }
        const element = receiptRef.current;
        runCopyAction(() => copyElementToClipboard(element));
    };

    // The same order summary already sent to Telegram (see
    // handleTelegramReceipt below), just without the Telegram-specific
    // <b>/<a> markup so it reads correctly once pasted somewhere else.
    const handleCopyText = () => {
        if (!order) {
            alert("Receipt not ready to copy. Please try again.");
            return;
        }
        runCopyAction(() =>
            navigator.clipboard.writeText(
                telegramService.formatOrderMessagePlainText(order),
            ),
        );
    };

    const handleTelegramReceipt = async () => {
        if (order) {
            try {
                await telegramService.sendOrderToGroup(order);
                alert("Receipt sent to Telegram successfully!");
            } catch (error) {
                console.error("Failed to send receipt to Telegram:", error);
                alert(
                    `Failed to send receipt to Telegram. ${
                        error instanceof Error ? error.message : ""
                    }`.trim(),
                );
            }
        }
    };

    if (!order) return null;

    // const change = amountPaid - order.total_amount;
    const orderDate = order.order_date
        ? new Date(order.order_date)
        : order.create_time
          ? new Date(order.create_time)
          : new Date();

    // Gift items are hidden from the customer-facing bill unless the order
    // was explicitly marked to show them. This same list drives both the
    // on-screen preview and the printed copy (handlePrint clones this DOM).
    const visibleItems =
        order.items?.filter(
            (item) => order.show_gifts_on_bill || !item.is_gift,
        ) || [];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth transitionDuration={autoPrint ? 0 : undefined}>
            <DialogTitle>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h6">
                        Receipt - {order.order_number}
                    </Typography>
                    <Button onClick={onClose} size="small">
                        <Close />
                    </Button>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Paper
                    ref={receiptRef}
                    sx={{
                        p: 2, // Reduced from 3 to 2 (16px total, 8px per side)
                        pt: "22px",
                        pb: "22px",
                        fontFamily: "Arial, Helvetica, sans-serif",
                        fontSize: "12px",
                        lineHeight: 1.4,
                        maxWidth: 300,
                        margin: "0 auto",
                        backgroundColor: "#fff",
                        // Decorative "torn off the thermal printer" zigzag
                        // edges, screen/share view only — this is styling on
                        // the Paper element itself (its own class/style
                        // attributes), never on the Paper's CHILDREN, so it
                        // can't leak into the actual print: handlePrint()
                        // below only clones receiptRef.current.innerHTML
                        // (the children's markup), not this element's own
                        // attributes. Plain background gradients (no
                        // clip-path/pseudo-elements) so "Copy as Photo"
                        // (html2canvas) renders it reliably too.
                        backgroundImage: [
                            "linear-gradient(135deg, #bbb 50%, transparent 50%)",
                            "linear-gradient(-135deg, #bbb 50%, transparent 50%)",
                            "linear-gradient(45deg, #bbb 50%, transparent 50%)",
                            "linear-gradient(-45deg, #bbb 50%, transparent 50%)",
                        ].join(", "),
                        backgroundPosition:
                            "top left, top left, bottom left, bottom left",
                        backgroundSize: "16px 12px",
                        backgroundRepeat: "repeat-x, repeat-x, repeat-x, repeat-x",
                        "& *": { fontFamily: "inherit" },
                        // Add CSS classes for consistent styling
                        "& .header": {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "20px",
                            minHeight: "60px",
                        },
                        "& .logo-section": {
                            flex: "0 0 auto",
                            width: "25%",
                            textAlign: "center",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        },
                        "& .content-section": {
                            flex: "1 1 auto",
                            width: "75%",
                            paddingLeft: "8px",
                            textAlign: "left",
                            borderLeft: "1px solid #ccc",
                        },
                        "& .shop-name": {
                            fontSize: "18px",
                            fontWeight: "bold",
                            marginBottom: "5px",
                        },
                        "& .shop-info": {
                            fontSize: "10px",
                            marginBottom: "2px",
                        },
                        "& .divider": {
                            borderTop: "1px dashed #000",
                            margin: "10px 0",
                        },
                    }}
                >
                    <div className="receipt">
                        {/* Header with Logo */}
                        <Box className="header">
                            {/* Logo Section (1/4 width) */}
                            <Box className="logo-section">
                                <img
                                    src={RECEIPT_CONFIG.LOGO.URL}
                                    alt="Catelyn Pet Shop Logo"
                                    style={{
                                        width: RECEIPT_CONFIG.LOGO.WIDTH_PX,
                                        height: RECEIPT_CONFIG.LOGO.HEIGHT_PX,
                                        objectFit: "contain",
                                    }}
                                />
                            </Box>

                            {/* Content Section (3/4 width) */}
                            <Box className="content-section">
                                <Typography className="shop-name">
                                    {RECEIPT_CONFIG.SHOP_INFO.NAME}
                                </Typography>
                                <Typography className="shop-info">
                                    {RECEIPT_CONFIG.SHOP_INFO.ADDRESS}
                                </Typography>
                                <Typography className="shop-info">
                                    Phone: {RECEIPT_CONFIG.SHOP_INFO.PHONE}
                                </Typography>
                                <Typography className="shop-info">
                                    Email: {RECEIPT_CONFIG.SHOP_INFO.EMAIL}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider
                            className="divider"
                            sx={{ borderStyle: "dashed", my: 2 }}
                        />

                        {/* Order Info */}
                        <Box className="order-info" sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: "12px", mb: 0.5 }}>
                                <strong>Date:</strong>{" "}
                                {orderDate.toLocaleDateString("en-GB")}{" "}
                                {orderDate.toLocaleTimeString("en-GB", {
                                    hour12: false,
                                })}
                            </Typography>
                            {customer && (
                                <Typography sx={{ fontSize: "12px", mb: 0.5 }}>
                                    <strong>Customer:</strong>{" "}
                                    {getCustomerFullName(customer)}
                                </Typography>
                            )}
                            {customer?.phone && (
                                <Typography sx={{ fontSize: "12px", mb: 0.5 }}>
                                    <strong>Phone:</strong> {customer.phone}
                                </Typography>
                            )}
                            {customer?.email && (
                                <Typography sx={{ fontSize: "12px", mb: 0.5 }}>
                                    <strong>Email:</strong> {customer.email}
                                </Typography>
                            )}
                            {order.shipping_address &&
                                order.shipping_address !==
                                    "In-store pickup" && (
                                    <Typography
                                        sx={{ fontSize: "12px", mb: 0.5 }}
                                    >
                                        <strong>Shipping:</strong>{" "}
                                        {order.shipping_address}
                                    </Typography>
                                )}
                        </Box>

                        <Divider
                            className="divider"
                            sx={{ borderStyle: "dashed", my: 2 }}
                        />

                        {/* Items */}
                        <Box className="items-table" sx={{ mb: 2 }}>
                            <Table size="small">
                                <TableBody>
                                    {visibleItems.map((item, index) => (
                                        <React.Fragment key={index}>
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        border: "none",
                                                        p: 0,
                                                        pb: 1,
                                                    }}
                                                    colSpan={3}
                                                >
                                                    <Typography
                                                        className="item-name"
                                                        sx={{
                                                            fontWeight: "bold",
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        {item.product?.name ||
                                                            "Product"}
                                                        {item.is_gift &&
                                                            " (Gift)"}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        border: "none",
                                                        p: 0,
                                                        fontSize: "11px",
                                                        color: "#666",
                                                    }}
                                                >
                                                    {item.quantity} x{" "}
                                                    {item.is_gift ? (
                                                        "FREE"
                                                    ) : item.list_price &&
                                                      item.list_price >
                                                          item.unit_price ? (
                                                        <>
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    textDecoration:
                                                                        "line-through",
                                                                    color: "#999",
                                                                    mr: 0.5,
                                                                }}
                                                            >
                                                                {formatCurrencyBDT(
                                                                    item.list_price,
                                                                )}
                                                            </Box>
                                                            {formatCurrencyBDT(
                                                                item.unit_price,
                                                            )}
                                                        </>
                                                    ) : (
                                                        formatCurrencyBDT(
                                                            item.unit_price,
                                                        )
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        border: "none",
                                                        p: 0,
                                                    }}
                                                ></TableCell>
                                                <TableCell
                                                    sx={{
                                                        border: "none",
                                                        p: 0,
                                                        textAlign: "right",
                                                        fontSize: "12px",
                                                    }}
                                                >
                                                    {item.is_gift
                                                        ? "FREE"
                                                        : formatCurrencyBDT(
                                                              item.total_price,
                                                          )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        border: "none",
                                                        p: 0,
                                                        pb: 2,
                                                    }}
                                                    colSpan={3}
                                                ></TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>

                        <Divider
                            className="divider"
                            sx={{ borderStyle: "dashed", my: 2 }}
                        />

                        {/* Totals */}
                        <Box className="totals">
                            <Table size="small" sx={{ width: "100%" }}>
                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                border: "none",
                                                p: 0,
                                                fontSize: "11px",
                                                width: "60%",
                                            }}
                                        >
                                            Items Total:
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                border: "none",
                                                p: 0,
                                                fontSize: "11px",
                                                textAlign: "right",
                                                width: "40%",
                                            }}
                                        >
                                            {formatCurrencyBDT(
                                                visibleItems.reduce(
                                                    (total, item) =>
                                                        total +
                                                        item.quantity *
                                                            item.unit_price,
                                                    0,
                                                ),
                                            )}
                                        </TableCell>
                                    </TableRow>

                                    {(order.shipping_amount || 0) > 0 && (
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    border: "none",
                                                    p: 0,
                                                    fontSize: "11px",
                                                }}
                                            >
                                                Delivery:
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    border: "none",
                                                    p: 0,
                                                    fontSize: "11px",
                                                    textAlign: "right",
                                                }}
                                            >
                                                {formatCurrencyBDT(
                                                    order.shipping_amount || 0,
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {(order.discount_amount || 0) > 0 && (
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    border: "none",
                                                    p: 0,
                                                    fontSize: "11px",
                                                }}
                                            >
                                                Discount:
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    border: "none",
                                                    p: 0,
                                                    fontSize: "11px",
                                                    textAlign: "right",
                                                }}
                                            >
                                                -
                                                {formatCurrencyBDT(
                                                    order.discount_amount || 0,
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    <TableRow className="total-row">
                                        <TableCell
                                            sx={{
                                                border: "none",
                                                p: 0,
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                                borderTop: "1px solid #000",
                                                paddingTop: "10px",
                                                width: "60%",
                                            }}
                                        >
                                            TOTAL:
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                border: "none",
                                                p: 0,
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                                borderTop: "1px solid #000",
                                                paddingTop: "10px",
                                                textAlign: "right",
                                                width: "40%",
                                            }}
                                        >
                                            {formatCurrencyBDT(
                                                order.total_amount,
                                            )}
                                        </TableCell>
                                    </TableRow>

                                    {/* Payment Details */}
                                    {order.payment_transactions &&
                                    order.payment_transactions.length > 0 ? (
                                        <>
                                            <TableRow>
                                                <TableCell
                                                    colSpan={2}
                                                    sx={{
                                                        border: "none",
                                                        p: 0,
                                                        pt: 2,
                                                        fontSize: "11px",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    Payment Details:
                                                </TableCell>
                                            </TableRow>
                                            {order.payment_transactions.map(
                                                (
                                                    payment: any,
                                                    index: number,
                                                ) => (
                                                    <TableRow key={index}>
                                                        <TableCell
                                                            sx={{
                                                                border: "none",
                                                                p: 0,
                                                                fontSize:
                                                                    "11px",
                                                                width: "60%",
                                                            }}
                                                        >
                                                            {
                                                                payment.payment_method
                                                            }
                                                            {payment.reference_number &&
                                                                ` (${payment.reference_number})`}
                                                            :
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                border: "none",
                                                                p: 0,
                                                                fontSize:
                                                                    "11px",
                                                                textAlign:
                                                                    "right",
                                                                width: "40%",
                                                            }}
                                                        >
                                                            {formatCurrencyBDT(
                                                                payment.amount,
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ),
                                            )}
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        border: "none",
                                                        p: 0,
                                                        pt: 1,
                                                        fontSize: "11px",
                                                        fontWeight: "bold",
                                                        width: "60%",
                                                    }}
                                                >
                                                    Total Paid:
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        border: "none",
                                                        p: 0,
                                                        pt: 1,
                                                        fontSize: "11px",
                                                        fontWeight: "bold",
                                                        textAlign: "right",
                                                        width: "40%",
                                                    }}
                                                >
                                                    {formatCurrencyBDT(
                                                        order.amount_paid || 0,
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            {order.amount_due > 0 && (
                                                <TableRow>
                                                    <TableCell
                                                        sx={{
                                                            border: "none",
                                                            p: 0,
                                                            fontSize: "13px",
                                                            fontWeight: "bold",
                                                            color: "error.main",
                                                            width: "60%",
                                                        }}
                                                    >
                                                        DUE:
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            border: "none",
                                                            p: 0,
                                                            fontSize: "13px",
                                                            fontWeight: "bold",
                                                            textAlign: "right",
                                                            color: "error.main",
                                                            width: "40%",
                                                        }}
                                                    >
                                                        {formatCurrencyBDT(
                                                            order.amount_due,
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {order.amount_paid >
                                                order.total_amount && (
                                                <TableRow>
                                                    <TableCell
                                                        sx={{
                                                            border: "none",
                                                            p: 0,
                                                            fontSize: "11px",
                                                            fontWeight: "bold",
                                                            width: "60%",
                                                        }}
                                                    >
                                                        Change:
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            border: "none",
                                                            p: 0,
                                                            fontSize: "11px",
                                                            fontWeight: "bold",
                                                            textAlign: "right",
                                                            width: "40%",
                                                        }}
                                                    >
                                                        {formatCurrencyBDT(
                                                            order.amount_paid -
                                                                order.total_amount,
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    ) : (
                                        // Show DUE even when no payment transactions
                                        order.amount_due > 0 && (
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        border: "none",
                                                        p: 0,
                                                        pt: 2,
                                                        fontSize: "13px",
                                                        fontWeight: "bold",
                                                        color: "error.main",
                                                        width: "60%",
                                                    }}
                                                >
                                                    DUE:
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        border: "none",
                                                        p: 0,
                                                        pt: 2,
                                                        fontSize: "13px",
                                                        fontWeight: "bold",
                                                        textAlign: "right",
                                                        color: "error.main",
                                                        width: "40%",
                                                    }}
                                                >
                                                    {formatCurrencyBDT(
                                                        order.amount_due,
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </Box>

                        <Divider
                            className="divider"
                            sx={{ borderStyle: "dashed", my: 3 }}
                        />

                        {/* Footer */}
                        <Box
                            className="footer"
                            sx={{ textAlign: "center", mt: 3 }}
                        >
                            <Typography sx={{ fontSize: "10px", mb: 1 }}>
                                Thank you for shopping with us!
                            </Typography>
                            <Typography sx={{ fontSize: "10px", mb: 1 }}>
                                Visit us again soon!
                            </Typography>
                            <Typography sx={{ fontSize: "10px", mb: 1 }}>
                                For support: catelynpetshop@gmail.com
                            </Typography>
                        </Box>
                    </div>
                </Paper>
            </DialogContent>

            <DialogActions
                sx={{
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        onClick={handleTelegramReceipt}
                        startIcon={<Send />}
                        size="small"
                        variant="outlined"
                    ></Button>
                    {onCreateCourier && (
                        <Button
                            onClick={onCreateCourier}
                            startIcon={<LocalShipping />}
                            size="small"
                            variant="outlined"
                            color="warning"
                        ></Button>
                    )}
                    {onDuplicateOrder && (
                        <Button
                            onClick={onDuplicateOrder}
                            startIcon={<ContentCopy />}
                            size="small"
                            variant="outlined"
                            color="info"
                        ></Button>
                    )}
                    {onEdit && (
                        <Button
                            onClick={onEdit}
                            startIcon={<Edit />}
                            size="small"
                            variant="outlined"
                            color="primary"
                        ></Button>
                    )}
                    {onUpdateStatus && order?.status !== "CANCELLED" && (
                        <Button
                            onClick={onUpdateStatus}
                            startIcon={<Edit />}
                            size="small"
                            variant="outlined"
                            color="warning"
                        >
                            {order?.status || "Update"}
                        </Button>
                    )}
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {/* Icon-only like its siblings, but Tooltip'd — unlike
                        Print/Edit/etc., "copy to clipboard" isn't a
                        universally obvious icon on its own. Opens a menu
                        rather than copying directly, since there are now
                        two things "copy" could mean here. */}
                    <Tooltip title={copiedReceipt ? "Copied!" : "Copy receipt"}>
                        <span>
                            <Button
                                onClick={(e) => setCopyMenuAnchor(e.currentTarget)}
                                startIcon={
                                    copyingReceipt ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : copiedReceipt ? (
                                        <CheckCircle />
                                    ) : (
                                        <ImageIcon />
                                    )
                                }
                                variant="outlined"
                                color={copiedReceipt ? "success" : "primary"}
                                disabled={copyingReceipt}
                            ></Button>
                        </span>
                    </Tooltip>
                    <Menu
                        anchorEl={copyMenuAnchor}
                        open={!!copyMenuAnchor}
                        onClose={() => setCopyMenuAnchor(null)}
                    >
                        <MenuItem
                            onClick={() => {
                                setCopyMenuAnchor(null);
                                handleCopyImage();
                            }}
                        >
                            <ListItemIcon>
                                <ImageIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Copy as Photo</ListItemText>
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                setCopyMenuAnchor(null);
                                handleCopyText();
                            }}
                        >
                            <ListItemIcon>
                                <TextSnippet fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Copy as Text</ListItemText>
                        </MenuItem>
                    </Menu>
                    <Tooltip title={`Print (${PRINT_SHORTCUT_LABEL})`}>
                        <Button
                            onClick={handlePrint}
                            startIcon={<Print />}
                            variant="contained"
                            aria-label="Print receipt"
                        ></Button>
                    </Tooltip>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
